import React, { useState } from "react";
import axios from "axios";
import "../style.css"; // Ensure the style path is correct
import Title from "../title.png";

// ResultSection Component
const ResultSection = ({
  trials,
  userId,
  diagnosis,
  diagnosisStage,
  symptoms,
}) => {
  const handleInteraction = async (trialId, action, recommendation) => {
    try {
      await axios.post("/api/track-interaction", {
        trialId: trialId,
        action: action,
        recommendation: recommendation || null,
        userId: userId,
        diagnosis,
        diagnosisStage,
        symptoms,
      });
    } catch (error) {
      console.error("Error tracking interaction", error);
    }
  };
  // Function to extract NCTId and clean the title
  const extractNCTIdAndCleanTitle = (title) => {
    const nctIdMatch = title.match(/^NCT\d+\s*-\s*/);
    const nctId = nctIdMatch ? nctIdMatch[0].split("-")[0].trim() : "";
    const cleanTitle = title.replace(nctIdMatch, "").trim();
    return { nctId, cleanTitle };
  };
  return (
    <div className="result-section">
      {trials.length === 0 ? (
        <p>No trials available</p>
      ) : (
        trials.map((trial, index) => {
          const { title, summary } = trial;
          const { nctId, cleanTitle } = extractNCTIdAndCleanTitle(title);
          // Debugging logs
          console.log(`Trial #${index}:`);
          console.log(`NCTId: ${nctId}`);
          console.log(`Title: ${cleanTitle}`);
          const trialURL = `https://clinicaltrials.gov/study/${nctId}`;
          console.log(`URL: ${trialURL}`);
          return (
            <div key={index} className="trial-item">
              <div className="trial-header">
                <h3>
                  <a
                    href={trialURL} // Correct hyperlink
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {cleanTitle}
                  </a>
                </h3>
              </div>
              <p className="personalized-summary">{summary}</p>
              <button
                className="contact-btn"
                onClick={() => handleInteraction(nctId, "contact", trial)}
              >
                Get in Touch
              </button>
              <div className="button-gap"></div>
              <details
                onClick={() => handleInteraction(nctId, "learn_more", trial)}
              >
                <summary>Learn More</summary>
                <div className="additional-info">
                  {trial.briefOverview && (
                    <p>
                      <strong>Brief Overview:</strong> {trial.briefOverview}
                    </p>
                  )}
                  {trial.condition && (
                    <p>
                      <strong>Condition:</strong> {trial.condition}
                    </p>
                  )}
                  {trial.intervention && (
                    <p>
                      <strong>Intervention:</strong> {trial.intervention}
                    </p>
                  )}
                  {trial.studyType && (
                    <p>
                      <strong>Study Type:</strong> {trial.studyType}
                    </p>
                  )}
                  {trial.phase && (
                    <p>
                      <strong>Phase:</strong> {trial.phase}
                    </p>
                  )}
                  {trial.estimatedEnrollment && (
                    <p>
                      <strong>Estimated Enrollment:</strong>
                      {trial.estimatedEnrollment}
                    </p>
                  )}
                  {trial.ageEligibility && (
                    <p>
                      <strong>Age Eligibility:</strong>
                      {trial.ageEligibility}
                    </p>
                  )}
                  {trial.genderEligibility && (
                    <p>
                      <strong>Gender Eligibility:</strong>
                      {trial.genderEligibility}
                    </p>
                  )}
                  {trial.keyOutcomeMeasures && (
                    <p>
                      <strong>Key Outcome Measures:</strong>
                      {trial.keyOutcomeMeasures}
                    </p>
                  )}
                  {trial.locations && (
                    <p>
                      <strong>Locations:</strong> {trial.locations}
                    </p>
                  )}
                  {trial.startDate && (
                    <p>
                      <strong>Start Date:</strong> {trial.startDate}
                    </p>
                  )}
                  {trial.exclusionCriteria && (
                    <p>
                      <strong>Exclusion Criteria:</strong>
                      {trial.exclusionCriteria}
                    </p>
                  )}
                </div>
              </details>
            </div>
          );
        })
      )}
    </div>
  );
};

function App() {
  const [recommendations, setRecommendations] = useState({
    interventional: [],
    observational: [],
  });
  const [activeSection, setActiveSection] = useState("interventional");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [userId, setUserId] = useState(null);
  const [userInput, setUserInput] = useState({
    diagnosis: "",
    diagnosisStage: "",
    symptoms: "",
  }); // Store user input

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setIsSubmitting(true);
    setError(null);
    setHasSubmitted(true);
    const formData = {
      diagnosis: event.target.diagnosis.value,
      diagnosisStage: event.target.diagnosisStage.value,
      symptoms: event.target.symptoms.value,
    };
    try {
      const response = await axios.post("/api/find-trials", formData);
      if (response.data && response.data.recommendations) {
        setUserId(response.data.userId);
        setUserInput(formData);
        setRecommendations(response.data.recommendations);
        event.target.reset();
      } else {
        throw new Error("Unexpected response format");
      }
    } catch (error) {
      console.error("Error fetching trials:", error);
      setError(
        "Something went wrong while finding your trials. Please try again.",
      );
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleSectionToggle = (section) => {
    setActiveSection(section);
  };

  return (
    <div className="App">
      <div className="header-container">
        <header className="app-header">
          <img src={Title} alt="Title" className="title" />
        </header>
      </div>
      <div className="container">
        <div className="input-section">
          <h3>Please enter your information below</h3>
          <form onSubmit={handleFormSubmit}>
            <div className="input-group">
              <input
                name="diagnosis"
                id="diagnosis"
                placeholder="Your Diagnosis"
                required
              />
            </div>
            <div className="input-group">
              <input
                name="diagnosisStage"
                id="diagnosisStage"
                placeholder="Stage of Diagnosis"
                required
              />
            </div>
            <div className="input-group">
              <input
                name="symptoms"
                id="symptoms"
                placeholder="Describe Your Symptoms (Optional)"
              />
            </div>
            <button
              type="submit"
              className="glow-button"
              id="find-trials-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? "FINDING TRIALS..." : "FIND MY TRIALS"}
            </button>
          </form>
          <div className="feedback-note"></div>
        </div>
        <div className="results-section">
          {isLoading ? (
            <div className="message-box">
              <h4 className="loading">
                Searching for the best trials for you...
              </h4>
            </div>
          ) : !hasSubmitted ? (
            <div className="message-box">
              <h4>Your trials will appear here</h4>
            </div>
          ) : error ||
            (!recommendations.interventional.length &&
              !recommendations.observational.length) ? (
            <div className="message-box">
              <h4>No applicable trials</h4>
            </div>
          ) : recommendations.interventional.length > 0 ||
            recommendations.observational.length > 0 ? (
            <div className="recommendations-container">
              <div className="recommendations-content">
                <h2>Your Trial Recommendations</h2>
                <div className="section-buttons">
                  <button
                    className={`section-button ${
                      activeSection === "interventional" ? "active" : ""
                    }`}
                    onClick={() => handleSectionToggle("interventional")}
                  >
                    Interventional
                  </button>
                  <button
                    className={`section-button ${
                      activeSection === "observational" ? "active" : ""
                    }`}
                    onClick={() => handleSectionToggle("observational")}
                  >
                    Observational
                  </button>
                </div>
                {activeSection === "interventional" && (
                  <ResultSection
                    title="Interventional Studies"
                    trials={recommendations.interventional}
                    userId={userId}
                    diagnosis={userInput.diagnosis}
                    diagnosisStage={userInput.diagnosisStage}
                    symptoms={userInput.symptoms}
                  />
                )}
                {activeSection === "observational" && (
                  <ResultSection
                    title="Observational Studies"
                    trials={recommendations.observational}
                    userId={userId}
                    diagnosis={userInput.diagnosis}
                    diagnosisStage={userInput.diagnosisStage}
                    symptoms={userInput.symptoms}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="message-box">
              <h4>No applicable trials</h4>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
export default App;
